import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "@NP-Webber/crm-kit";
import { shellApi } from "../../api/shellApi";
import {
  AddOutlinedIcon,
  ArrowBackOutlinedIcon,
  CloseFullscreenOutlinedIcon,
  CloseOutlinedIcon,
  DeleteOutlinedIcon,
  InfoOutlinedIcon,
  NotificationsOutlinedIcon,
  OpenInFullOutlinedIcon,
  SendOutlinedIcon,
} from "../../ui/appIcons";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { ChatComposerEmojiPicker } from "./ChatComposerEmojiPicker";
import { ChatReplyPreview } from "./ChatReplyPreview";
import { CreateChatDialog } from "./CreateChatDialog";
import { GroupMembersModal } from "./GroupMembersModal";
import { PendingChatsModal } from "./PendingChatsModal";
import { TreatmentQueueList } from "./TreatmentQueueList";
import { conversationDisplayName, useChat } from "./ChatProvider";

type TabKey = "all" | "direct" | "groups" | "treatment";

export function ChatWindow() {
  const {
    canSend,
    canManageTreatment,
    conversations,
    pending,
    activeConversationId,
    setActiveConversationId,
    messages,
    openConversation,
    sendMessage,
    currentUserId,
    typingLabel,
    emitTyping,
    reloadConversations,
    setIsOpen,
    replyTo,
    setReplyTo,
    highlightMessageId,
    toggleReaction,
    deleteMessage,
    markUnreadFromMessage,
    setTreatment,
    scrollToMessage,
    treatmentOpenBadge,
    treatmentQueueTick,
  } = useChat();

  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const active = conversations.find((c) => c.id === activeConversationId) ?? null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations.filter((c) => {
      if (tab === "direct" && c.isGroup) return false;
      if (tab === "groups" && !c.isGroup) return false;
      if (!q) return true;
      const title = conversationDisplayName(c, currentUserId).toLowerCase();
      const preview = c.lastMessage?.content?.toLowerCase() ?? "";
      return title.includes(q) || preview.includes(q);
    });
  }, [conversations, tab, search, currentUserId]);

  const width = expanded ? Math.min(window.innerWidth - 48, 820) : 380;
  const height = expanded
    ? Math.min(window.innerHeight - 48, 700)
    : Math.min(window.innerHeight - 180, 520);

  useEffect(() => {
    if (!active) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [active, messages.length]);

  const handleSend = async () => {
    if (!canSend || !text.trim() || sending) return;
    setSending(true);
    try {
      await sendMessage(text);
      setText("");
      emitTyping(false);
    } finally {
      setSending(false);
    }
  };

  const tabs = (
    [
      ["all", "הכל"],
      ["direct", "ישיר"],
      ["groups", "קבוצות"],
      ...(canManageTreatment ? ([["treatment", "לטיפול"]] as const) : []),
    ] as const
  );

  return (
    <>
      <div
        className={`chat-window${expanded ? " chat-window--expanded" : ""}`}
        style={{ width, height }}
        role="dialog"
        aria-label="צ׳אט פנימי"
      >
        <header className="chat-window__header">
          {active ? (
            <>
              <button
                type="button"
                className="chat-window__icon-btn"
                aria-label="חזרה"
                onClick={() => {
                  setActiveConversationId(null);
                  setReplyTo(null);
                }}
              >
                <ArrowBackOutlinedIcon />
              </button>
              <div className="chat-window__title">
                {conversationDisplayName(active, currentUserId)}
                {typingLabel ? (
                  <span className="chat-window__typing">{typingLabel}</span>
                ) : null}
              </div>
              {active.isGroup ? (
                <button
                  type="button"
                  className="chat-window__icon-btn"
                  aria-label="משתתפים"
                  onClick={() => setMembersOpen(true)}
                >
                  <InfoOutlinedIcon />
                </button>
              ) : null}
              <button
                type="button"
                className="chat-window__icon-btn"
                aria-label="מחיקת שיחה"
                onClick={() => setLeaveOpen(true)}
              >
                <DeleteOutlinedIcon />
              </button>
            </>
          ) : (
            <>
              <div className="chat-window__title">צ׳אט</div>
              <button
                type="button"
                className="chat-window__icon-btn chat-window__icon-btn--badge"
                aria-label="הזמנות ממתינות"
                onClick={() => setPendingOpen(true)}
              >
                <NotificationsOutlinedIcon />
                {pending.length > 0 ? (
                  <span className="chat-window__mini-badge">{pending.length}</span>
                ) : null}
              </button>
              {canSend ? (
                <button
                  type="button"
                  className="chat-window__icon-btn"
                  aria-label="שיחה חדשה"
                  onClick={() => setCreateOpen(true)}
                >
                  <AddOutlinedIcon />
                </button>
              ) : null}
            </>
          )}
          <button
            type="button"
            className="chat-window__icon-btn"
            aria-label={expanded ? "כיווץ" : "הרחבה"}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <CloseFullscreenOutlinedIcon /> : <OpenInFullOutlinedIcon />}
          </button>
          <button
            type="button"
            className="chat-window__icon-btn"
            aria-label="סגור"
            onClick={() => {
              setIsOpen(false);
              setActiveConversationId(null);
              setReplyTo(null);
            }}
          >
            <CloseOutlinedIcon />
          </button>
        </header>

        {!active ? (
          <div className="chat-window__body">
            <div className="chat-window__tabs" role="tablist">
              {tabs.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`chat-window__tab${tab === key ? " is-active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  {label}
                  {key === "treatment" && treatmentOpenBadge > 0 ? (
                    <span className="chat-window__tab-badge">{treatmentOpenBadge}</span>
                  ) : null}
                </button>
              ))}
            </div>
            {tab === "treatment" ? (
              <TreatmentQueueList
                refreshKey={treatmentQueueTick}
                onOpenMessage={(conversationId, messageId) => {
                  void openConversation(conversationId, messageId);
                }}
              />
            ) : (
              <>
                <input
                  className="chat-window__search"
                  type="search"
                  placeholder="חיפוש שיחות…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <ul className="chat-window__list">
                  {filtered.length === 0 ? (
                    <li className="chat-window__empty">אין שיחות</li>
                  ) : (
                    filtered.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="chat-window__conv"
                          onClick={() => void openConversation(c.id)}
                        >
                          <div className="chat-window__conv-main">
                            <strong>{conversationDisplayName(c, currentUserId)}</strong>
                            <span className="chat-window__conv-preview">
                              {c.lastMessage?.attachmentName
                                ? "קובץ"
                                : c.lastMessage?.content || "אין הודעות"}
                            </span>
                          </div>
                          {(c.unreadCount ?? 0) > 0 ? (
                            <span className="chat-window__conv-unread">{c.unreadCount}</span>
                          ) : null}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </>
            )}
          </div>
        ) : (
          <div className="chat-window__thread">
            <div className="chat-window__messages">
              {messages.map((m) => (
                <ChatMessageBubble
                  key={m.id}
                  message={m}
                  mine={m.senderUserId === currentUserId}
                  canSend={canSend}
                  canManageTreatment={canManageTreatment}
                  currentUserId={currentUserId}
                  highlighted={highlightMessageId === m.id}
                  onReply={setReplyTo}
                  onReact={(messageId, emoji) => void toggleReaction(messageId, emoji)}
                  onDelete={(messageId) => void deleteMessage(messageId)}
                  onMarkUnread={(messageId) => void markUnreadFromMessage(messageId)}
                  onSetTreatment={(messageId, status) =>
                    void setTreatment(messageId, status)
                  }
                  onScrollToReply={scrollToMessage}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
            {canSend ? (
              <div className="chat-window__composer-wrap">
                {replyTo ? (
                  <ChatReplyPreview
                    snapshot={{
                      replyToMessageId: replyTo.id,
                      senderName:
                        replyTo.senderUserId === currentUserId
                          ? "אני"
                          : active?.members?.find((m) => m.userId === replyTo.senderUserId)
                              ?.displayName || "משתמש",
                      contentPreview: replyTo.content,
                      isDeleted: false,
                    }}
                    onCancel={() => setReplyTo(null)}
                  />
                ) : null}
                <div className="chat-window__composer">
                  <ChatComposerEmojiPicker
                    disabled={sending}
                    onPick={(emoji) => {
                      setText((prev) => `${prev}${emoji}`);
                      emitTyping(true);
                    }}
                  />
                  <input
                    type="text"
                    placeholder="כתבו הודעה…"
                    value={text}
                    onChange={(e) => {
                      setText(e.target.value);
                      emitTyping(e.target.value.length > 0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void handleSend();
                      }
                    }}
                    disabled={sending}
                  />
                  <button
                    type="button"
                    className="chat-window__send"
                    aria-label="שליחה"
                    disabled={sending || !text.trim()}
                    onClick={() => void handleSend()}
                  >
                    <SendOutlinedIcon />
                  </button>
                </div>
              </div>
            ) : (
              <div className="chat-window__readonly">אין הרשאת שליחה</div>
            )}
          </div>
        )}
      </div>

      <CreateChatDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        canSend={canSend}
        onCreated={(id) => {
          void reloadConversations().then(() => openConversation(id));
        }}
      />
      <PendingChatsModal open={pendingOpen} onClose={() => setPendingOpen(false)} />
      <GroupMembersModal
        open={membersOpen}
        conversationId={activeConversationId}
        onClose={() => setMembersOpen(false)}
      />
      <ConfirmDialog
        open={leaveOpen}
        title="הסרת שיחה"
        message="להסיר את השיחה מהרשימה שלך?"
        confirmText="הסרה"
        cancelText="ביטול"
        onConfirm={() => {
          if (!activeConversationId) return;
          void shellApi.leaveConversation(activeConversationId).then(() => {
            setLeaveOpen(false);
            setActiveConversationId(null);
            void reloadConversations();
          });
        }}
        onCancel={() => setLeaveOpen(false)}
        onClose={() => setLeaveOpen(false)}
      />
    </>
  );
}
